import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  Badge,
  VStack,
  HStack,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Textarea,
  Checkbox,
} from '@chakra-ui/react'
import { Field } from '@/components/ui/field'
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select'
import { Key, Calculator, Info, ChevronRight, ChevronLeft, ChevronsRight, ChevronsLeft, X } from 'lucide-react'
import { useDistributionKey, useCreateDistributionKey, useUpdateDistributionKey, useUpdateDistributionKeyUnits } from '../hooks'
import { useUnits } from '@/features/units/hooks/useUnits'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type {
  DistributionCalculationBase,
  DistributionKeyType,
  MeterType,
  MeterReadingFrequency,
  MissingReadingAllocationMethod
} from '../types'
import {
  METER_TYPE_LABELS,
  METER_TYPE_UNITS,
  METER_READING_FREQUENCY_LABELS,
  MISSING_READING_METHOD_LABELS
} from '../types'

interface DistributionKeyModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
  distributionKeyId?: string
}

interface UnitShare {
  unit_id: string
  unit_number: string
  stairwell?: string | null
  floor?: number | null
  unit_type?: string | null
  general_shares: number
  area_sqm?: number | null
  share_value: number
  is_included: boolean
}

// Labels des types de lot
const UNIT_TYPE_LABELS: Record<string, { label: string; short: string; color: string }> = {
  RESIDENTIAL: { label: 'Résidentiel', short: 'Rés', color: 'blue' },
  PARKING: { label: 'Parking', short: 'Park', color: 'orange' },
  CELLAR: { label: 'Cave', short: 'Cave', color: 'purple' },
  COMMERCIAL: { label: 'Commercial', short: 'Com', color: 'green' },
}

const CALCULATION_BASES: { value: DistributionCalculationBase; label: string; description: string; unit: string }[] = [
  { value: 'shares', label: 'Tantièmes', description: 'Répartition selon les tantièmes généraux', unit: 'tantièmes' },
  { value: 'surface', label: 'Surface', description: 'Répartition selon la surface des lots', unit: 'm²' },
  { value: 'fixed', label: 'Parts égales', description: 'Répartition égale entre tous les lots', unit: 'parts' },
  { value: 'weighted', label: 'Pondérée', description: 'Valeurs personnalisées par lot', unit: 'points' },
  { value: 'variable', label: 'Variable (compteurs)', description: 'Basée sur des relevés périodiques', unit: 'variable' },
]

const KEY_TYPES: { value: DistributionKeyType; label: string }[] = [
  { value: 'general', label: 'Générale' },
  { value: 'special', label: 'Spéciale' },
]

export function DistributionKeyModal({
  isOpen,
  onClose,
  residenceId,
  distributionKeyId,
}: DistributionKeyModalProps) {
  const isEditMode = !!distributionKeyId
  const { data: existingKey, isLoading: loadingKey } = useDistributionKey(distributionKeyId)
  const { data: units, isLoading: loadingUnits } = useUnits(residenceId)

  const createKey = useCreateDistributionKey()
  const updateKey = useUpdateDistributionKey()
  const updateUnits = useUpdateDistributionKeyUnits()

  const [formData, setFormData] = useState({
    name: '',
    key_type: 'special' as DistributionKeyType,
    calculation_base: 'shares' as DistributionCalculationBase,
    description: '',
    // Nouveaux champs pour les clés variables
    meter_type: undefined as MeterType | undefined,
    meter_unit: '',
    meter_reading_frequency: 'quarterly' as MeterReadingFrequency,
    missing_reading_method: 'shares' as MissingReadingAllocationMethod,
  })

  const [unitShares, setUnitShares] = useState<UnitShare[]>([])

  // Filtres multi-select (tags)
  const [selectedStairwells, setSelectedStairwells] = useState<string[]>([])
  const [selectedFloors, setSelectedFloors] = useState<number[]>([])
  const [selectedUnitTypes, setSelectedUnitTypes] = useState<string[]>([])

  // Sélections pour transfert
  const [leftSelected, setLeftSelected] = useState<Set<string>>(new Set())
  const [rightSelected, setRightSelected] = useState<Set<string>>(new Set())

  // Initialiser les données à l'ouverture de la modal
  useEffect(() => {
    if (!isOpen) return

    if (isEditMode && existingKey) {
      setFormData({
        name: existingKey.name,
        key_type: existingKey.key_type,
        calculation_base: existingKey.calculation_base,
        description: existingKey.description || '',
        meter_type: existingKey.meter_type as MeterType | undefined,
        meter_unit: existingKey.meter_unit || '',
        meter_reading_frequency: (existingKey.meter_reading_frequency as MeterReadingFrequency) || 'quarterly',
        missing_reading_method: (existingKey.missing_reading_method as MissingReadingAllocationMethod) || 'shares',
      })

      if (existingKey.distribution_key_units && units) {
        const existingUnitsMap = new Map(
          existingKey.distribution_key_units.map((dku) => [dku.unit_id, dku])
        )
        setUnitShares(
          units.map((unit) => {
            const existing = existingUnitsMap.get(unit.id)
            return {
              unit_id: unit.id,
              unit_number: unit.unit_number,
              stairwell: unit.stairwell,
              floor: unit.floor,
              unit_type: unit.unit_type,
              general_shares: unit.general_shares ?? 0,
              area_sqm: unit.area_sqm,
              share_value: existing?.share_value ?? unit.general_shares ?? 0,
              is_included: existing?.is_included ?? true,
            }
          })
        )
      }
    } else if (!isEditMode && units) {
      setUnitShares(
        units.map((unit) => ({
          unit_id: unit.id,
          unit_number: unit.unit_number,
          stairwell: unit.stairwell,
          floor: unit.floor,
          unit_type: unit.unit_type,
          general_shares: unit.general_shares ?? 0,
          area_sqm: unit.area_sqm,
          share_value: unit.general_shares ?? 0,
          is_included: false,
        }))
      )
    }
  }, [isOpen, isEditMode, existingKey, units])

  const isWeighted = formData.calculation_base === 'weighted'
  const currentUnit = CALCULATION_BASES.find((cb) => cb.value === formData.calculation_base)?.unit || ''

  // Total des quotes-parts (calculé dynamiquement selon la base)
  const totalShares = useMemo(() => {
    const included = unitShares.filter((us) => us.is_included)
    switch (formData.calculation_base) {
      case 'shares':
        return included.reduce((sum, us) => sum + us.general_shares, 0)
      case 'surface':
        return included.reduce((sum, us) => sum + (us.area_sqm ?? 0), 0)
      case 'fixed':
        return included.length
      case 'weighted':
        return included.reduce((sum, us) => sum + us.share_value, 0)
      default:
        return 0
    }
  }, [unitShares, formData.calculation_base])

  // Listes disponibles
  const availableStairwells = useMemo(() => {
    return [...new Set(unitShares.map((us) => us.stairwell).filter(Boolean) as string[])].sort()
  }, [unitShares])

  const availableFloors = useMemo(() => {
    return [...new Set(unitShares.map((us) => us.floor).filter((f) => f !== null && f !== undefined) as number[])].sort((a, b) => a - b)
  }, [unitShares])

  const availableUnitTypes = useMemo(() => {
    return [...new Set(unitShares.map((us) => us.unit_type).filter(Boolean) as string[])].sort()
  }, [unitShares])

  // Lots exclus et inclus (sans tri par sélection pour éviter boucle infinie)
  const excludedUnitsBase = useMemo(() => unitShares.filter((us) => !us.is_included), [unitShares])
  const includedUnitsBase = useMemo(() => unitShares.filter((us) => us.is_included), [unitShares])

  // Versions triées pour l'affichage (sélectionnés en haut)
  const excludedUnits = useMemo(() => {
    return [...excludedUnitsBase].sort((a, b) => {
      const aSelected = leftSelected.has(a.unit_id) ? 0 : 1
      const bSelected = leftSelected.has(b.unit_id) ? 0 : 1
      if (aSelected !== bSelected) return aSelected - bSelected
      return a.unit_number.localeCompare(b.unit_number)
    })
  }, [excludedUnitsBase, leftSelected])

  const includedUnits = useMemo(() => {
    return [...includedUnitsBase].sort((a, b) => {
      const aSelected = rightSelected.has(a.unit_id) ? 0 : 1
      const bSelected = rightSelected.has(b.unit_id) ? 0 : 1
      if (aSelected !== bSelected) return aSelected - bSelected
      return a.unit_number.localeCompare(b.unit_number)
    })
  }, [includedUnitsBase, rightSelected])

  // Effet: quand les filtres changent, sélectionner automatiquement les lots correspondants dans les deux colonnes
  useEffect(() => {
    if (selectedStairwells.length === 0 && selectedFloors.length === 0 && selectedUnitTypes.length === 0) {
      setLeftSelected(new Set())
      setRightSelected(new Set())
      return
    }

    const matchFilter = (us: UnitShare) => {
      const matchStairwell = selectedStairwells.length === 0 || selectedStairwells.includes(us.stairwell || '')
      const matchFloor = selectedFloors.length === 0 || selectedFloors.includes(us.floor ?? -999)
      const matchUnitType = selectedUnitTypes.length === 0 || selectedUnitTypes.includes(us.unit_type || '')
      return matchStairwell && matchFloor && matchUnitType
    }

    const matchingLeft = excludedUnitsBase.filter(matchFilter)
    const matchingRight = includedUnitsBase.filter(matchFilter)

    setLeftSelected(new Set(matchingLeft.map((us) => us.unit_id)))
    setRightSelected(new Set(matchingRight.map((us) => us.unit_id)))
  }, [selectedStairwells, selectedFloors, selectedUnitTypes, excludedUnitsBase, includedUnitsBase])

  // Toggle filtres
  const toggleStairwellFilter = (sw: string) => {
    setSelectedStairwells((prev) =>
      prev.includes(sw) ? prev.filter((s) => s !== sw) : [...prev, sw]
    )
  }

  const toggleFloorFilter = (fl: number) => {
    setSelectedFloors((prev) =>
      prev.includes(fl) ? prev.filter((f) => f !== fl) : [...prev, fl]
    )
  }

  const toggleUnitTypeFilter = (type: string) => {
    setSelectedUnitTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    )
  }

  const clearFilters = () => {
    setSelectedStairwells([])
    setSelectedFloors([])
    setSelectedUnitTypes([])
  }

  // Actions dual-list
  const moveToRight = () => {
    if (leftSelected.size === 0) return
    setUnitShares((prev) =>
      prev.map((us) => leftSelected.has(us.unit_id) ? { ...us, is_included: true } : us)
    )
    setLeftSelected(new Set())
    clearFilters()
  }

  const moveToLeft = () => {
    if (rightSelected.size === 0) return
    setUnitShares((prev) =>
      prev.map((us) => rightSelected.has(us.unit_id) ? { ...us, is_included: false } : us)
    )
    setRightSelected(new Set())
  }

  const moveAllToRight = () => {
    setUnitShares((prev) => prev.map((us) => ({ ...us, is_included: true })))
    setLeftSelected(new Set())
    clearFilters()
  }

  const moveAllToLeft = () => {
    setUnitShares((prev) => prev.map((us) => ({ ...us, is_included: false })))
    setRightSelected(new Set())
  }

  const toggleLeftSelect = (id: string) => {
    setLeftSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleRightSelect = (id: string) => {
    setRightSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAllLeft = () => setLeftSelected(new Set(excludedUnits.map((us) => us.unit_id)))
  const deselectAllLeft = () => setLeftSelected(new Set())
  const selectAllRight = () => setRightSelected(new Set(includedUnits.map((us) => us.unit_id)))
  const deselectAllRight = () => setRightSelected(new Set())

  const handleShareChange = (unitId: string, value: number) => {
    setUnitShares((prev) =>
      prev.map((us) => us.unit_id === unitId ? { ...us, share_value: value } : us)
    )
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toaster.create({ title: 'Erreur', description: 'Le nom est obligatoire', type: 'error' })
      return
    }

    // Validation spécifique pour les clés variables
    if (formData.calculation_base === 'variable') {
      if (!formData.meter_type) {
        toaster.create({ title: 'Erreur', description: 'Le type de compteur est obligatoire pour les clés variables', type: 'error' })
        return
      }
      if (!formData.meter_unit) {
        toaster.create({ title: 'Erreur', description: 'L\'unité de mesure est obligatoire pour les clés variables', type: 'error' })
        return
      }
    }

    const includedCount = unitShares.filter((us) => us.is_included).length
    if (includedCount === 0) {
      toaster.create({ title: 'Erreur', description: 'Vous devez inclure au moins un lot', type: 'error' })
      return
    }

    if (totalShares === 0 && formData.calculation_base !== 'variable') {
      toaster.create({ title: 'Erreur', description: 'Le total des quotes-parts ne peut pas être nul', type: 'error' })
      return
    }

    try {
      let keyId = distributionKeyId

      const keyData = {
        name: formData.name,
        key_type: formData.key_type,
        calculation_base: formData.calculation_base,
        description: formData.description || null,
        total_base: totalShares,
        // Nouveaux champs pour clés variables
        meter_type: formData.calculation_base === 'variable' ? (formData.meter_type || null) : null,
        meter_unit: formData.calculation_base === 'variable' ? (formData.meter_unit || null) : null,
        meter_reading_frequency: formData.calculation_base === 'variable' ? (formData.meter_reading_frequency || null) : null,
        missing_reading_method: formData.calculation_base === 'variable' ? (formData.missing_reading_method || null) : null,
      }

      if (isEditMode) {
        await updateKey.mutateAsync({
          id: distributionKeyId!,
          ...keyData,
        })
      } else {
        const result = await createKey.mutateAsync({
          residence_id: residenceId,
          ...keyData,
          is_active: true,
          created_by: null,
          special_share_type: null,
          conditions: null,
        })
        keyId = result.id
      }

      await updateUnits.mutateAsync({
        distributionKeyId: keyId!,
        units: unitShares.map((us) => ({
          unit_id: us.unit_id,
          share_value: us.share_value,
          is_included: us.is_included,
        })),
        calculationBase: formData.calculation_base,
      })

      toaster.create({
        title: 'Succès',
        description: isEditMode ? 'Clé de répartition modifiée' : 'Clé de répartition créée',
        type: 'success',
      })
      handleClose()
    } catch (error) {
      let errorMessage = 'Erreur lors de l\'enregistrement'

      if (error instanceof Error) {
        // Détecter l'erreur de nom en double (contrainte UNIQUE)
        if (error.message.includes('distribution_keys_unique_name') || error.message.includes('duplicate key')) {
          errorMessage = 'Une clé de répartition avec ce nom existe déjà pour cette résidence'
        } else {
          errorMessage = error.message
        }
      }

      toaster.create({
        title: 'Erreur',
        description: errorMessage,
        type: 'error',
      })
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      key_type: 'special',
      calculation_base: 'shares',
      description: '',
      meter_type: undefined,
      meter_unit: '',
      meter_reading_frequency: 'quarterly',
      missing_reading_method: 'shares',
    })
    setUnitShares([])
    setSelectedStairwells([])
    setSelectedFloors([])
    setLeftSelected(new Set())
    setRightSelected(new Set())
    onClose()
  }

  const formatFloor = (fl: number | null | undefined) => {
    if (fl === null || fl === undefined) return '-'
    if (fl < 0) return `SS${Math.abs(fl)}`
    if (fl === 0) return 'RDC'
    return fl.toString()
  }

  const isLoading = loadingKey || loadingUnits
  const isPending = createKey.isPending || updateKey.isPending || updateUnits.isPending

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="xl">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="950px" maxH="90vh">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              <Flex align="center" gap={2}>
                <Key size={20} />
                {isEditMode ? 'Modifier la clé de répartition' : 'Nouvelle clé de répartition'}
              </Flex>
            </DialogTitle>
          </DialogHeader>
          <DialogBody overflowY="auto">
            {isLoading ? (
              <Text>Chargement...</Text>
            ) : (
              <VStack align="stretch" gap={4}>
                {/* Informations générales */}
                <Flex gap={4} wrap="wrap">
                  <Box flex={2} minW="200px">
                    <Field label="Nom de la clé" required>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Ex: Ascenseur bâtiment A"
                        size="md"
                      />
                    </Field>
                  </Box>
                  <Box flex={1} minW="150px">
                    <Field label="Type">
                      <NativeSelectRoot size="md">
                        <NativeSelectField
                          value={formData.key_type}
                          onChange={(e) => setFormData({ ...formData, key_type: e.target.value as DistributionKeyType })}
                        >
                          {KEY_TYPES.map((kt) => (
                            <option key={kt.value} value={kt.value}>{kt.label}</option>
                          ))}
                        </NativeSelectField>
                      </NativeSelectRoot>
                    </Field>
                  </Box>
                  <Box flex={1} minW="150px">
                    <Field label="Base de calcul">
                      <NativeSelectRoot size="md">
                        <NativeSelectField
                          value={formData.calculation_base}
                          onChange={(e) => setFormData({ ...formData, calculation_base: e.target.value as DistributionCalculationBase })}
                        >
                          {CALCULATION_BASES.map((cb) => (
                            <option key={cb.value} value={cb.value}>{cb.label}</option>
                          ))}
                        </NativeSelectField>
                      </NativeSelectRoot>
                    </Field>
                  </Box>
                </Flex>

                <Box p={3} bg="blue.50" borderRadius="md">
                  <Flex align="center" gap={2}>
                    <Info size={16} color="var(--chakra-colors-blue-600)" />
                    <Text fontSize="sm" color="blue.700">
                      {CALCULATION_BASES.find((cb) => cb.value === formData.calculation_base)?.description}
                    </Text>
                  </Flex>
                </Box>

                <Field label="Description (optionnel)">
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description ou conditions d'application..."
                    rows={2}
                  />
                </Field>

                {/* Configuration clé variable */}
                {formData.calculation_base === 'variable' && (
                  <>
                    <Text fontWeight="semibold" mt={4}>Configuration des relevés</Text>

                    <Flex gap={4} wrap="wrap">
                      <Box flex={1} minW="200px">
                        <Field label="Type de compteur" required>
                          <NativeSelectRoot size="md">
                            <NativeSelectField
                              value={formData.meter_type || ''}
                              onChange={(e) => {
                                const type = e.target.value as MeterType
                                setFormData({
                                  ...formData,
                                  meter_type: type,
                                  meter_unit: METER_TYPE_UNITS[type]
                                })
                              }}
                            >
                              <option value="">Sélectionner...</option>
                              {Object.entries(METER_TYPE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </NativeSelectField>
                          </NativeSelectRoot>
                        </Field>
                      </Box>

                      <Box flex={1} minW="150px">
                        <Field label="Unité de mesure">
                          <Input
                            value={formData.meter_unit || ''}
                            disabled
                            placeholder="Auto"
                            size="md"
                          />
                        </Field>
                      </Box>

                      <Box flex={1} minW="150px">
                        <Field label="Fréquence de relevé">
                          <NativeSelectRoot size="md">
                            <NativeSelectField
                              value={formData.meter_reading_frequency}
                              onChange={(e) => setFormData({ ...formData, meter_reading_frequency: e.target.value as MeterReadingFrequency })}
                            >
                              {Object.entries(METER_READING_FREQUENCY_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </NativeSelectField>
                          </NativeSelectRoot>
                        </Field>
                      </Box>
                    </Flex>

                    <Box p={3} bg="orange.50" borderRadius="md" mt={3}>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Répartition en cas de relevés manquants
                      </Text>
                      <Text fontSize="xs" color="gray.600" mb={2}>
                        Si certains lots n'ont pas de relevés, le reste du montant sera réparti selon :
                      </Text>
                      <NativeSelectRoot size="sm">
                        <NativeSelectField
                          value={formData.missing_reading_method}
                          onChange={(e) => setFormData({ ...formData, missing_reading_method: e.target.value as MissingReadingAllocationMethod })}
                        >
                          {Object.entries(MISSING_READING_METHOD_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </NativeSelectField>
                      </NativeSelectRoot>
                    </Box>

                    <Box p={3} bg="blue.50" borderRadius="md" mt={2}>
                      <Flex align="center" gap={2}>
                        <Info size={16} color="var(--chakra-colors-blue-600)" />
                        <Text fontSize="sm" color="blue.700">
                          Des alertes vous rappelleront de saisir les relevés selon la fréquence choisie.
                        </Text>
                      </Flex>
                    </Box>
                  </>
                )}

                {/* Header avec total */}
                <Flex justify="space-between" align="center">
                  <Text fontWeight="semibold">Sélection des lots</Text>
                  <HStack>
                    <Calculator size={16} />
                    <Text fontSize="sm">
                      {includedUnits.length} lot(s) inclus - Total : <strong>{totalShares.toLocaleString('fr-FR')} {currentUnit}</strong>
                    </Text>
                  </HStack>
                </Flex>

                {/* Filtres rapides - impacte les deux colonnes */}
                <Box p={2} bg="gray.50" borderRadius="md">
                  <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={2}>Sélection rapide</Text>
                  <Flex gap={4} wrap="wrap">
                    {availableUnitTypes.length > 1 && (
                      <Box>
                        <Text fontSize="xs" color="gray.500" mb={1}>Type de lot</Text>
                        <Flex gap={1} wrap="wrap">
                          {availableUnitTypes.map((type) => {
                            const typeInfo = UNIT_TYPE_LABELS[type] || { label: type, short: type, color: 'gray' }
                            return (
                              <Badge
                                key={type}
                                colorPalette={selectedUnitTypes.includes(type) ? typeInfo.color : 'gray'}
                                variant={selectedUnitTypes.includes(type) ? 'solid' : 'outline'}
                                cursor="pointer"
                                onClick={() => toggleUnitTypeFilter(type)}
                                px={2}
                                py={0.5}
                                fontSize="xs"
                              >
                                {typeInfo.short}
                                {selectedUnitTypes.includes(type) && <X size={10} style={{ marginLeft: 2 }} />}
                              </Badge>
                            )
                          })}
                        </Flex>
                      </Box>
                    )}
                    {availableStairwells.length > 0 && (
                      <Box>
                        <Text fontSize="xs" color="gray.500" mb={1}>Escaliers</Text>
                        <Flex gap={1} wrap="wrap">
                          {availableStairwells.map((sw) => (
                            <Badge
                              key={sw}
                              colorPalette={selectedStairwells.includes(sw) ? 'blue' : 'gray'}
                              variant={selectedStairwells.includes(sw) ? 'solid' : 'outline'}
                              cursor="pointer"
                              onClick={() => toggleStairwellFilter(sw)}
                              px={2}
                              py={0.5}
                              fontSize="xs"
                            >
                              {sw}
                              {selectedStairwells.includes(sw) && <X size={10} style={{ marginLeft: 2 }} />}
                            </Badge>
                          ))}
                        </Flex>
                      </Box>
                    )}
                    {availableFloors.length > 0 && (
                      <Box flex={1}>
                        <Text fontSize="xs" color="gray.500" mb={1}>Étages</Text>
                        <Flex gap={1} wrap="wrap">
                          {availableFloors.map((fl) => (
                            <Badge
                              key={fl}
                              colorPalette={selectedFloors.includes(fl) ? 'purple' : 'gray'}
                              variant={selectedFloors.includes(fl) ? 'solid' : 'outline'}
                              cursor="pointer"
                              onClick={() => toggleFloorFilter(fl)}
                              px={2}
                              py={0.5}
                              fontSize="xs"
                            >
                              {formatFloor(fl)}
                              {selectedFloors.includes(fl) && <X size={10} style={{ marginLeft: 2 }} />}
                            </Badge>
                          ))}
                        </Flex>
                      </Box>
                    )}
                  </Flex>
                  {(selectedStairwells.length > 0 || selectedFloors.length > 0 || selectedUnitTypes.length > 0) && (
                    <Flex mt={2} justify="space-between" align="center">
                      <Text fontSize="xs" color="blue.600">
                        {leftSelected.size + rightSelected.size} lot(s) correspondant(s) sélectionné(s)
                      </Text>
                      <Button size="xs" variant="ghost" onClick={clearFilters}>Effacer les filtres</Button>
                    </Flex>
                  )}
                </Box>

                {/* Dual-list */}
                <Flex gap={2}>
                  {/* Liste gauche - Lots disponibles */}
                  <Box flex={1} borderWidth="1px" borderRadius="md" overflow="hidden">
                    <Flex bg="gray.100" px={2} py={1.5} borderBottomWidth="1px" align="center">
                      <Checkbox.Root
                        checked={
                          leftSelected.size > 0 && leftSelected.size < excludedUnits.length
                            ? 'indeterminate'
                            : excludedUnits.length > 0 && leftSelected.size === excludedUnits.length
                        }
                        onCheckedChange={(e) => e.checked ? selectAllLeft() : deselectAllLeft()}
                        size="sm"
                        disabled={excludedUnits.length === 0}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                      </Checkbox.Root>
                      <Text fontSize="sm" fontWeight="medium" ml={2}>Disponibles ({excludedUnits.length})</Text>
                    </Flex>
                    <Box maxH="220px" overflowY="auto">
                      {excludedUnits.length === 0 ? (
                        <Text fontSize="sm" color="gray.500" p={3} textAlign="center">Tous les lots sont inclus</Text>
                      ) : (
                        excludedUnits.map((us) => (
                          <Checkbox.Root
                            key={us.unit_id}
                            checked={leftSelected.has(us.unit_id)}
                            onCheckedChange={() => toggleLeftSelect(us.unit_id)}
                            size="sm"
                            width="100%"
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Label
                              display="flex"
                              alignItems="center"
                              width="100%"
                              px={2}
                              py={1}
                              borderBottomWidth="1px"
                              borderColor="gray.100"
                              cursor="pointer"
                              bg={leftSelected.has(us.unit_id) ? 'blue.50' : 'white'}
                              _hover={{ bg: leftSelected.has(us.unit_id) ? 'blue.100' : 'gray.50' }}
                            >
                              <Checkbox.Control />
                              <Text fontSize="sm" ml={2} flex={1}>{us.unit_number}</Text>
                              {us.unit_type && UNIT_TYPE_LABELS[us.unit_type] && (
                                <Badge size="sm" colorPalette={UNIT_TYPE_LABELS[us.unit_type]!.color} variant="subtle" mr={1}>
                                  {UNIT_TYPE_LABELS[us.unit_type]!.short}
                                </Badge>
                              )}
                              {us.stairwell && <Badge size="sm" variant="outline">{us.stairwell}</Badge>}
                              <Text fontSize="xs" color="gray.500" ml={1} w="30px" textAlign="right">{formatFloor(us.floor)}</Text>
                            </Checkbox.Label>
                          </Checkbox.Root>
                        ))
                      )}
                    </Box>
                  </Box>

                  {/* Boutons de transfert */}
                  <VStack justify="center" gap={1}>
                    <Button size="xs" variant="outline" onClick={moveAllToRight} title="Tout ajouter">
                      <ChevronsRight size={16} />
                    </Button>
                    <Button size="xs" variant="outline" onClick={moveToRight} disabled={leftSelected.size === 0} title="Ajouter sélection">
                      <ChevronRight size={16} />
                    </Button>
                    <Button size="xs" variant="outline" onClick={moveToLeft} disabled={rightSelected.size === 0} title="Retirer sélection">
                      <ChevronLeft size={16} />
                    </Button>
                    <Button size="xs" variant="outline" onClick={moveAllToLeft} title="Tout retirer">
                      <ChevronsLeft size={16} />
                    </Button>
                  </VStack>

                  {/* Liste droite - Lots inclus */}
                  <Box flex={1} borderWidth="1px" borderRadius="md" overflow="hidden">
                    <Flex bg="green.50" px={2} py={1.5} borderBottomWidth="1px" align="center">
                      <Checkbox.Root
                        checked={
                          rightSelected.size > 0 && rightSelected.size < includedUnits.length
                            ? 'indeterminate'
                            : includedUnits.length > 0 && rightSelected.size === includedUnits.length
                        }
                        onCheckedChange={(e) => e.checked ? selectAllRight() : deselectAllRight()}
                        size="sm"
                        disabled={includedUnits.length === 0}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                      </Checkbox.Root>
                      <Text fontSize="sm" fontWeight="medium" color="green.700" ml={2}>Inclus ({includedUnits.length})</Text>
                    </Flex>
                    <Box maxH="220px" overflowY="auto">
                      {includedUnits.length === 0 ? (
                        <Text fontSize="sm" color="gray.500" p={3} textAlign="center">Aucun lot inclus</Text>
                      ) : (
                        includedUnits.map((us) => (
                          <Checkbox.Root
                            key={us.unit_id}
                            checked={rightSelected.has(us.unit_id)}
                            onCheckedChange={() => toggleRightSelect(us.unit_id)}
                            size="sm"
                            width="100%"
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Label
                              display="flex"
                              alignItems="center"
                              width="100%"
                              px={2}
                              py={1}
                              borderBottomWidth="1px"
                              borderColor="gray.100"
                              cursor="pointer"
                              bg={rightSelected.has(us.unit_id) ? 'green.100' : 'white'}
                              _hover={{ bg: rightSelected.has(us.unit_id) ? 'green.200' : 'gray.50' }}
                            >
                              <Checkbox.Control />
                              <Text fontSize="sm" ml={2} flex={1}>{us.unit_number}</Text>
                              {us.unit_type && UNIT_TYPE_LABELS[us.unit_type] && (
                                <Badge size="sm" colorPalette={UNIT_TYPE_LABELS[us.unit_type]!.color} variant="subtle" mr={1}>
                                  {UNIT_TYPE_LABELS[us.unit_type]!.short}
                                </Badge>
                              )}
                              {us.stairwell && <Badge size="sm" variant="outline">{us.stairwell}</Badge>}
                              <Text fontSize="xs" color="gray.500" ml={1} w="30px" textAlign="right">{formatFloor(us.floor)}</Text>
                              {isWeighted ? (
                                <Input
                                  type="number"
                                  size="xs"
                                  w="60px"
                                  ml={2}
                                  textAlign="right"
                                  value={us.share_value}
                                  onChange={(e) => handleShareChange(us.unit_id, parseFloat(e.target.value) || 0)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <Text fontSize="xs" color="gray.600" ml={2} w="50px" textAlign="right" fontFamily="mono">
                                  {formData.calculation_base === 'shares' && us.general_shares}
                                  {formData.calculation_base === 'surface' && (us.area_sqm ?? 0)}
                                  {formData.calculation_base === 'fixed' && 1}
                                </Text>
                              )}
                            </Checkbox.Label>
                          </Checkbox.Root>
                        ))
                      )}
                    </Box>
                  </Box>
                </Flex>
              </VStack>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              Annuler
            </Button>
            <Button colorPalette="brand" onClick={handleSubmit} loading={isPending}>
              {isEditMode ? 'Enregistrer' : 'Créer la clé'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
